import { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthHeaders, isAdminLoggedIn, getAdminUser, extractCreatorId } from '../utils/authUtils';
import { getApiBaseUrlWithoutPath } from '../../config/environment';
import { hasAccess } from '../constants/teamAccessPermissions';
import { extractAdminListTotal } from '../utils/adminListTotal';
import { fetchAdminProductTotal } from '../utils/fetchAdminProductTotal';

const useMenuCounts = () => {
  const hasLoadedOnceRef = useRef(false);
  const [counts, setCounts] = useState({
    totalProducts: 0,
    totalUsers: 0,
    totalOrders: 0,
    totalCategories: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCounts = useCallback(async () => {
    // Only fetch if user is authenticated
    if (!isAdminLoggedIn()) {
      console.log('🔒 User not authenticated, skipping counts fetch');
      return;
    }

    try {
      const silent = hasLoadedOnceRef.current;
      if (!silent) setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();
      const adminUser = getAdminUser();
      const isTeamMember = adminUser?.role === 'team';
      const skipUsersCountEndpoint =
        isTeamMember && !hasAccess('Users|View', adminUser);

      console.log('📊 Fetching menu counts from:', apiUrl);

      const productsCountUrl = `${apiUrl}/api/admin/products?limit=1&page=1`;

      // Fetch all counts in parallel (backend should return pagination.total)
      // Team members without Users|View get 403 on user count routes — skip to avoid console noise.
      const [productsRes, usersRes, ordersRes, categoriesRes] = await Promise.allSettled([
        fetch(productsCountUrl, { method: 'GET', headers }),
        skipUsersCountEndpoint
          ? Promise.resolve({ ok: false, status: 403 })
          : fetch(`${apiUrl}/api/admin/users/count/active`, { method: 'GET', headers }),
        fetch(`${apiUrl}/api/admin/orders?limit=1&page=1`, { method: 'GET', headers }),
        fetch(`${apiUrl}/api/categories`, { method: 'GET', headers })
      ]);

      const newCounts = {};

      if (productsRes.status === 'fulfilled' && productsRes.value.ok) {
        const productsData = await productsRes.value.json();
        let total = extractAdminListTotal(productsData);
        if (total == null) {
          const fallbackTotal = await fetchAdminProductTotal(headers).catch(() => null);
          if (fallbackTotal != null) total = fallbackTotal;
        }
        if (isTeamMember && (total == null || total <= 1)) {
          const myId = String(adminUser._id || adminUser.id || '');
          const rows = Array.isArray(productsData.data) ? productsData.data : [];
          if (myId && rows.length > 0) {
            const mineOnPage = rows.filter((p) => extractCreatorId(p.createdBy) === myId);
            const totalPages = Number(productsData.pagination?.totalPages ?? 1);
            if (totalPages <= 1 && mineOnPage.length > 0) {
              total = mineOnPage.length;
            }
          }
        }
        if (total != null) newCounts.totalProducts = total;
      }

      // Process users count (active users only)
      if (usersRes.status === 'fulfilled' && usersRes.value.ok) {
        const usersData = await usersRes.value.json();
        if (usersData.success) {
          newCounts.totalUsers = usersData.data?.count ?? usersData.count ?? 0;
        }
      }

      if (ordersRes.status === 'fulfilled' && ordersRes.value.ok) {
        const ordersData = await ordersRes.value.json();
        const total = extractAdminListTotal(ordersData);
        if (total != null) newCounts.totalOrders = total;
      }

      // Process categories count
      if (categoriesRes.status === 'fulfilled' && categoriesRes.value.ok) {
        const categoriesData = await categoriesRes.value.json();
        if (categoriesData.success) {
          const catData = categoriesData.data ?? categoriesData.categories ?? categoriesData;
          newCounts.totalCategories = Array.isArray(catData) ? catData.length : 0;
        }
      }

      console.log('✅ Menu counts loaded:', newCounts);
      setCounts((prev) => ({ ...prev, ...newCounts }));
      hasLoadedOnceRef.current = true;

    } catch (error) {
      console.error('❌ Counts error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch counts when component mounts and user is authenticated
    if (isAdminLoggedIn()) {
      fetchCounts();
    }
  }, [fetchCounts]);

  return { counts, loading, error, refetch: fetchCounts };
};

export default useMenuCounts;
